import { STACK_ERROR_SX } from "@components/common/dialog/faro-dialog";
import { GroupAutoComplete } from "@components/common/group-autocomplete/group-autocomplete";
import { LabelWithHelp } from "@components/common/label-with-help";
import { MembersAutocomplete } from "@components/common/members-autocomplete/members-autocomplete";
import { SphereAvatar } from "@components/header/sphere-avatar";
import { CompanyRoleAutoComplete } from "@components/role/role-autocomplete/company-role-autocomplete";
import {
  AutoCompleteHelperText,
  AutoCompleteOption,
  AutoCompleteSelectedOption,
  neutral,
  TextField,
  TeamIcon,
} from "@flat-ui";
import { Box, Grid2, Stack, Typography } from "@mui/material";
import {
  MAX_DESCRIPTION_CHARACTERS,
  MAX_NAME_CHARACTERS,
} from "@pages/members/teams/create-team-dialog";
import {
  ActionCreateNewTeam,
  StateCreateNewTeam,
} from "@pages/members/teams/create-team-reducer";
import {
  TEAM_DISPLAY_NAME,
  TEAMS_DISPLAY_NAME,
} from "@src/constants/team-constants";
import { capitalizeFirstLetter } from "@utils/string-utils";
import { isValidTeamName } from "@utils/team-utils";
import { Dispatch } from "react";

interface Props {
  /** All the states for the create new team */
  state: StateCreateNewTeam;

  /** All the dispatch for the create new team */
  dispatchTeams: Dispatch<ActionCreateNewTeam>;

  /** Whether the selected role is a group-level role */
  isGroupRoleSelected: boolean;

  /** Currently selected members in the autocomplete */
  selectedMembers: AutoCompleteSelectedOption[];

  /** Options to show in the members autocomplete */
  autocompleteOptions: AutoCompleteOption[];

  /** Validation message to display under the members autocomplete */
  helperText?: AutoCompleteHelperText;

  /** Updates the selected members */
  handleMemberSelect: (options: AutoCompleteSelectedOption[]) => void;

  /** Validates the text currently typed in the autocomplete input */
  onInputChange: (value: string) => void;

  /** Returns true when the typed email is valid and not already a member */
  isNewEmailValid: (value: string) => boolean;
}

/** The content for the dialog create team */
export function CreateTeamDialogContent({
  state,
  dispatchTeams,
  isGroupRoleSelected,
  selectedMembers,
  autocompleteOptions,
  helperText,
  handleMemberSelect,
  onInputChange,
  isNewEmailValid,
}: Props): JSX.Element {
  /**
   * Resets the group state when the selected role changes
   */
  function resetGroupsState(): void {
    dispatchTeams({ type: "SET_SELECTED_GROUP_ID", selectedGroupId: "" });
    dispatchTeams({ type: "SET_SELECTED_ROLE", selectedRole: undefined });
  }

  return (
    <Grid2 paddingX={"2px"}>
      <Grid2 container alignItems="center">
        <Grid2 size="grow">
          <Box borderBottom={`1px solid ${neutral[200]}`} width="100%" />
        </Grid2>

        <Grid2>
          <Box mx={2}>
            <SphereAvatar
              icon={<TeamIcon sx={{ width: "40px", height: "40px" }} />}
              size="x-large"
              backgroundColor={neutral[700]}
            />
          </Box>
        </Grid2>

        <Grid2 size="grow">
          <Box borderBottom={`1px solid ${neutral[200]}`} width="100%" />
        </Grid2>
      </Grid2>

      <Typography marginY={"32px"} fontSize={"14px"}>
        {capitalizeFirstLetter(TEAMS_DISPLAY_NAME)} allow you to organize
        individual members and assign roles to the entire {TEAM_DISPLAY_NAME} at
        once, saving time compared to assigning roles to each member
        individually.
      </Typography>

      {/* Team name */}
      <Stack sx={STACK_ERROR_SX}>
        <LabelWithHelp
          title={`${capitalizeFirstLetter(TEAM_DISPLAY_NAME)} name`}
          isRequired
        />
        <TextField
          maxCharacterCount={MAX_NAME_CHARACTERS}
          shouldShowCounter={true}
          onTextChanged={(name) => {
            dispatchTeams({ type: "SET_TEAM_NAME", teamName: name });
          }}
          text={state.teamName}
          fullWidth
          placeholder={`Enter the ${TEAM_DISPLAY_NAME} name...`}
          sx={{
            height: "38px",
            fontSize: "14px",
          }}
          autoFocus
          error={
            isValidTeamName(state.teamName)
              ? ""
              : `${capitalizeFirstLetter(
                  TEAM_DISPLAY_NAME
                )} name shouldn't contain comma(,), semicolon(;) or pipeline(|).`
          }
        />
      </Stack>

      {/* Workspace role */}
      <Stack sx={STACK_ERROR_SX}>
        <CompanyRoleAutoComplete
          selectedRole={state.selectedRole}
          onChange={(role) =>
            dispatchTeams({
              type: "SET_SELECTED_ROLE",
              selectedRole: role,
            })
          }
          placeholder="Assign a workspace role to your team if needed"
          onResetSelection={resetGroupsState}
          shouldAllowAllRolesSelection={true}
        />
      </Stack>

      {/* Group */}
      {isGroupRoleSelected && (
        <Stack sx={STACK_ERROR_SX}>
          <LabelWithHelp title="Group" isRequired />
          <GroupAutoComplete
            callbackOnGroupSelected={(groupId) =>
              dispatchTeams({
                type: "SET_SELECTED_GROUP_ID",
                selectedGroupId: groupId,
              })
            }
            selectedGroupId={state.selectedGroupId}
            placeholder="Please select a group..."
          />
        </Stack>
      )}

      {/* Team members */}
      <Stack sx={STACK_ERROR_SX}>
        <MembersAutocomplete
          label="Members"
          selectedOptions={selectedMembers}
          options={autocompleteOptions}
          helperText={helperText}
          // A new team has no existing members yet, so a typed email only needs
          // to be a valid email (no "already in team" check applies).
          isValid={isNewEmailValid}
          handleMemberSelect={handleMemberSelect}
          onInputChange={onInputChange}
          placeHolder="Enter email addresses (Separated by comma, semicolon, or space)"
        />
      </Stack>

      {/* Team description */}
      <LabelWithHelp title="Description" />
      <TextField
        maxCharacterCount={MAX_DESCRIPTION_CHARACTERS}
        shouldShowCounter={true}
        onTextChanged={(description) => {
          dispatchTeams({ type: "SET_DESCRIPTION", description });
        }}
        text={state.description}
        fullWidth
        placeholder={`Enter the ${TEAM_DISPLAY_NAME} description...`}
        multiline={true}
        rows={4}
        sx={{ fontSize: "14px" }}
      />
    </Grid2>
  );
}
